import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH } from "~/lib/viewport";

const LINK_PAD_LEFT = 12;

const DRAWER_PAD = 32;

const PROBE_Y = 30;

const STAGGER_MS = 60;

export default class WebNavbarController extends Controller<HTMLElement> {
  static override targets = [
    "desktop",
    "scrim",
    "tabs",
    "tab",
    "panel",
    "flap",
    "hamburger",
    "menu",
    "group",
    "stagger",
  ];
  declare readonly desktopTarget: HTMLElement;
  declare readonly hasDesktopTarget: boolean;
  declare readonly scrimTarget: HTMLElement;
  declare readonly hasScrimTarget: boolean;
  declare readonly tabsTarget: HTMLElement;
  declare readonly hasTabsTarget: boolean;
  declare readonly tabTargets: HTMLElement[];
  declare readonly panelTargets: HTMLElement[];
  declare readonly flapTarget: HTMLElement;
  declare readonly hasFlapTarget: boolean;
  declare readonly hamburgerTarget: HTMLElement;
  declare readonly hasHamburgerTarget: boolean;
  declare readonly menuTarget: HTMLElement;
  declare readonly hasMenuTarget: boolean;
  declare readonly groupTargets: HTMLElement[];
  declare readonly staggerTargets: HTMLElement[];

  private openTab: string | null = null;
  private scrolledPast = false;
  private overDark = false;
  private overLight = false;
  private menuOpen = false;
  private darkFrame = 0;
  private observers: { disconnect(): void }[] = [];
  private listeners = new AbortController();

  override connect(): void {
    const sentinel = document.querySelector(".web-navbar__sentinel");
    // No sentinel means no section wants a transparent bar over it, so the bar
    // is solid from first paint and stays there. That is the white theme.
    this.scrolledPast = sentinel === null;
    this.syncTreatment();

    if (sentinel !== null) {
      const solid = new IntersectionObserver(
        ([entry]) => {
          this.scrolledPast = entry !== undefined && !entry.isIntersecting;
          this.syncTreatment();
        },
        { threshold: 0 },
      );
      solid.observe(sentinel);
      this.observers.push(solid);
    }

    // Watching the attribute itself means the dark state can never stick: a
    // writer that lands after our frame still re-triggers a read.
    const marks = new MutationObserver(() => this.readDark());
    marks.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-nav-dark", "data-nav-light"],
      subtree: true,
    });
    this.observers.push(marks);
    this.readDark();

    const sizes = new ResizeObserver(() => this.measure());
    if (this.hasFlapTarget) sizes.observe(this.flapTarget);
    if (this.hasDesktopTarget) sizes.observe(this.desktopTarget);
    this.observers.push(sizes);
    this.measure();

    // Past the breakpoint the panel and the button are display: none, so
    // without this a resize could leave the menu open with nothing on screen
    // to close it.
    const desktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
    desktop.addEventListener(
      "change",
      (event) => {
        if (event.matches) this.setMenu(false);
      },
      { signal: this.listeners.signal },
    );
  }

  override disconnect(): void {
    for (const observer of this.observers) observer.disconnect();
    this.observers = [];
    this.listeners.abort();
    this.listeners = new AbortController();
    if (this.darkFrame) cancelAnimationFrame(this.darkFrame);
    this.darkFrame = 0;
  }

  // Desktop dropdowns ------------------------------------------------------

  toggleTab(event: Event): void {
    event.preventDefault();
    const name = (event.currentTarget as HTMLElement).dataset["tab"] ?? null;
    if (name === null || name === this.openTab) this.closeTab();
    else this.openTabNamed(name);
  }

  closeTab(): void {
    this.openTab = null;
    if (this.hasDesktopTarget) this.desktopTarget.classList.remove("is-open");
    for (const tab of this.tabTargets) tab.classList.remove("is-active");
    for (const panel of this.panelTargets) panel.classList.remove("is-active");
    if (this.hasScrimTarget) this.scrimTarget.classList.remove("is-open");
    this.syncTreatment();
  }

  closeTabOnOutsideClick(event: Event): void {
    if (this.openTab === null || !this.hasDesktopTarget) return;
    if (!this.desktopTarget.contains(event.target as Node)) this.closeTab();
  }

  closeOnEscape(): void {
    this.closeTab();
    this.setMenu(false);
  }

  private openTabNamed(name: string): void {
    this.openTab = name;
    if (this.hasDesktopTarget) this.desktopTarget.classList.add("is-open");
    for (const tab of this.tabTargets) {
      tab.classList.toggle("is-active", tab.dataset["tab"] === name);
    }
    for (const panel of this.panelTargets) {
      panel.classList.toggle("is-active", panel.dataset["panel"] === name);
    }
    if (this.hasScrimTarget) this.scrimTarget.classList.add("is-open");
    this.syncTreatment();
  }

  private syncTreatment(): void {
    const solid = this.menuOpen || this.scrolledPast || this.openTab !== null;
    const dark = this.overDark && !this.menuOpen;
    this.element.classList.toggle("is-dark", solid && dark);
    this.element.classList.toggle("is-light", solid && !dark);
    // A transparent bar is cream, for a hero with a clip or a dark field
    // behind it. Over a light one the type has to be ink instead, with the bar
    // itself still unpainted.
    this.element.classList.toggle("is-ink", !solid && this.overLight);
  }

  // Phone menu -------------------------------------------------------------
  //
  // `is-menu-open` goes on the root rather than on the panel because the
  // wordmark and the close button are siblings of it, not children: both have
  // to know the panel is up to pick their treatment.

  toggleMenu(): void {
    if (!this.hasMenuTarget) return;
    this.setMenu(!this.menuTarget.classList.contains("is-open"));
  }

  closeMenu(): void {
    this.setMenu(false);
  }

  /** One group at a time, which is what keeps the panel short enough not to
   * scroll. */
  toggleGroup(event: Event): void {
    const toggle = event.currentTarget as HTMLElement;
    const group = toggle.closest('[data-web-navbar-target="group"]');
    if (group === null) return;
    const opening = !group.classList.contains("is-open");
    this.closeGroups();
    group.classList.toggle("is-open", opening);
    toggle.setAttribute("aria-expanded", opening ? "true" : "false");
  }

  private setMenu(open: boolean): void {
    if (!this.hasMenuTarget) return;
    this.menuOpen = open;
    this.syncTreatment();
    this.menuTarget.classList.toggle("is-open", open);
    this.element.classList.toggle("is-menu-open", open);
    if (this.hasHamburgerTarget) {
      this.hamburgerTarget.setAttribute("aria-expanded", String(open));
    }
    this.staggerTargets.forEach((row, index) => {
      row.style.transitionDelay = open ? `${index * STAGGER_MS}ms` : "0ms";
    });
    if (!open) this.closeGroups();
  }

  private closeGroups(): void {
    for (const group of this.groupTargets) {
      group.classList.remove("is-open");
      group
        .querySelector("[aria-expanded]")
        ?.setAttribute("aria-expanded", "false");
    }
  }

  // Dark sections ----------------------------------------------------------

  /** Deferring to the next frame puts this after every synchronous scroll
   * handler, so a section that sets the attribute during its own scroll effect
   * has always landed by the time we look. */
  checkDark(): void {
    if (this.darkFrame) return;
    this.darkFrame = requestAnimationFrame(() => this.readDark());
  }

  private readDark(): void {
    this.darkFrame = 0;
    this.overDark = this.isOver("[data-nav-dark]");
    this.overLight = this.isOver("[data-nav-light]");
    this.syncTreatment();
  }

  private isOver(selector: string): boolean {
    return [...document.querySelectorAll(selector)].some((mark) => {
      const rect = mark.getBoundingClientRect();
      return rect.top <= PROBE_Y && rect.bottom > PROBE_Y;
    });
  }

  // Measured widths --------------------------------------------------------

  measure(): void {
    this.measureFlap();
    this.measureDrawer();
  }

  /** The bar ends just left of the flap. */
  private measureFlap(): void {
    if (!this.hasFlapTarget) return;
    // Below the breakpoint the flap is display: none, and measuring it there
    // would overwrite the desktop width with 0. Keep the last measurement.
    const width = this.flapTarget.offsetWidth;
    if (!width) return;
    this.element.style.setProperty("--web-navbar-flap-width", `${width}px`);
  }

  /** Aligns the open drawer's first link with the first nav tab. */
  private measureDrawer(): void {
    if (!this.hasDesktopTarget || !this.hasTabsTarget) return;
    const first = this.tabsTarget.firstElementChild;
    if (first === null) return;
    if (window.innerWidth < DESKTOP_MIN_WIDTH) {
      this.desktopTarget.style.removeProperty("--web-navbar-menu-width");
      return;
    }
    const navRect = this.desktopTarget.getBoundingClientRect();
    const tabPad = Number.parseFloat(getComputedStyle(first).paddingLeft) || 0;
    const textLeft =
      first.getBoundingClientRect().left -
      navRect.left +
      tabPad -
      LINK_PAD_LEFT;
    const width = Math.max(
      0,
      Math.round(navRect.width - DRAWER_PAD - textLeft),
    );
    this.desktopTarget.style.setProperty(
      "--web-navbar-menu-width",
      `${width}px`,
    );
  }
}
